import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Star, CheckCircle2, MessageSquarePlus } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function ReviewSubmitDialog({ triggerLabel = "Delite svoje mnenje" }) {
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", role: "", quote: "", rating: 5 });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || form.quote.length < 10) {
      toast.error("Izpolnite ime in vsaj nekaj besed mnenja (vsaj 10 znakov).");
      return;
    }
    setLoading(true);
    try {
      await api.post("/reviews", form);
      setSubmitted(true);
      toast.success("Hvala! Vaše mnenje je v pregledu.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setForm({ name: "", role: "", quote: "", rating: 5 });
    setSubmitted(false);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSubmitted(false); }}>
      <DialogTrigger asChild>
        <button
          type="button"
          data-testid="review-trigger"
          className="inline-flex items-center gap-2 rounded-full border border-[var(--vp-border)] bg-white px-5 py-2.5 text-sm font-semibold hover:border-[var(--vp-primary)]/40 transition-colors"
        >
          <MessageSquarePlus size={16} /> {triggerLabel}
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        {submitted ? (
          <div className="text-center py-4" data-testid="review-submitted">
            <CheckCircle2 className="mx-auto text-[var(--vp-primary)]" size={44} />
            <h3 className="font-display text-2xl font-bold mt-3">Hvala za mnenje!</h3>
            <p className="text-[var(--vp-muted)] mt-2 text-sm">
              Vaše mnenje bo objavljeno po kratkem pregledu.
            </p>
            <Button
              onClick={reset}
              data-testid="review-close"
              className="mt-5 rounded-full"
              style={{ background: "var(--vp-primary)", color: "white" }}
            >
              Zapri
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Delite svoje mnenje</DialogTitle>
              <DialogDescription>
                Vaša izkušnja pomaga drugim, ko izbirajo zavarovalnega svetovalca.
              </DialogDescription>
            </DialogHeader>

            <div>
              <Label className="text-sm font-semibold">Ocena</Label>
              <div className="mt-2 flex items-center gap-1" data-testid="review-rating">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    type="button"
                    key={n}
                    onClick={() => setForm({ ...form, rating: n })}
                    data-testid={`review-star-${n}`}
                    className="p-1 transition-transform hover:scale-110"
                    aria-label={`${n} zvezdic`}
                  >
                    <Star
                      size={26}
                      fill={n <= form.rating ? "#D08C60" : "transparent"}
                      stroke="#D08C60"
                    />
                  </button>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold">Ime *</Label>
                <Input
                  data-testid="review-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="npr. Maja K."
                  required
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold">Lokacija / vloga</Label>
                <Input
                  data-testid="review-role"
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  placeholder="npr. Ljubljana / SP"
                  className="mt-1.5"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold">Mnenje *</Label>
              <Textarea
                data-testid="review-quote"
                rows={4}
                value={form.quote}
                onChange={(e) => setForm({ ...form, quote: e.target.value })}
                placeholder="Kako je bila vaša izkušnja?"
                required
                className="mt-1.5"
              />
            </div>

            <DialogFooter>
              <Button
                type="submit"
                data-testid="review-submit"
                disabled={loading}
                className="w-full sm:w-auto rounded-full px-7 py-5 font-semibold"
                style={{ background: "var(--vp-primary)", color: "white" }}
              >
                {loading ? "Pošiljanje…" : "Pošlji mnenje"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
